// ==============================
// Dashboard customer search — a full-width bar under the quick-action cards.
// Admin searches the whole system by phone / name / email / id. It powers the
// "upsert" entry point: if a customer is FOUND, start service registration for
// that existing account; if NOT found, offer to create a new user / new org.
// Backed by GET /user/list?search= (already searches id, name, email, phone).
// ==============================

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PersonAddAlt1, Domain, Search } from '@mui/icons-material';

import { apiGet } from '../utils/apiClient';
import { PaginatedResponse, User } from '../types/UserTable.type';
import { userTypeColors } from '../utils/colorHandler';
import ColorChip from './common/ColorChip';
import { useTranslation } from '../i18n';
import type { QuickActionHandlers } from './QuickActions';

const displayName = (user: User): string =>
  user.businessName?.trim() ||
  `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim() ||
  `#${user.id}`;

// Classify the raw search text so the registration dialog can prefill the field
// the admin actually searched by (a phone or an email).
const isEmailTerm = (value: string): boolean => /^\S+@\S+\.\S+$/.test(value);
const isPhoneTerm = (value: string): boolean =>
  /^[+\d][\d\s.\-()]{5,}$/.test(value);

type Props = { handlers: QuickActionHandlers };

const QuickCustomerSearch = ({ handlers }: Props) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  // The query that produced `results` — so "not found" only shows once a search
  // has actually completed for the current text.
  const [searchedFor, setSearchedFor] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const term = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (term.length < 2) {
      setResults([]);
      setSearchedFor('');
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await apiGet<PaginatedResponse<User>>(
          `/user/list?page=1&limit=8&search=${encodeURIComponent(term)}`,
        );
        setResults(res.data);
        setSearchedFor(term);
      } catch {
        setResults([]);
        setSearchedFor(term);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const term = query.trim();
  const showNotFound =
    !loading &&
    term.length >= 2 &&
    searchedFor === term &&
    results.length === 0;

  return (
    <Card variant="outlined" sx={{ p: 2.5, mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} color="secondary.main">
        {t('home.search.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('home.search.subtitle')}
      </Typography>

      <TextField
        fullWidth
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('home.search.placeholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={18} />
            </InputAdornment>
          ) : null,
        }}
      />

      {results.length > 0 && (
        <List dense sx={{ mt: 1 }}>
          {results.map((user) => (
            <ListItemButton
              key={user.id}
              onClick={() =>
                handlers.startServiceForExistingUser({
                  id: user.id,
                  fullName: displayName(user),
                  userType: user.userType,
                  dob: user.dob,
                  // Prefill the field the admin searched by: prefer the record's
                  // own value, else fall back to the search term itself.
                  phone:
                    user.phone ||
                    (isPhoneTerm(term) ? term : '') ||
                    undefined,
                  email:
                    user.email ||
                    (isEmailTerm(term) ? term : '') ||
                    undefined,
                })
              }
              sx={{ borderRadius: 1 }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ width: '100%', flexWrap: 'wrap' }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {displayName(user)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{user.id}
                </Typography>
                <ColorChip label={user.userType} palette={userTypeColors} />
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {user.phone || '—'}
                  {user.email ? ` · ${user.email}` : ''}
                </Typography>
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={t('home.search.existing')}
                />
              </Stack>
            </ListItemButton>
          ))}
        </List>
      )}

      {showNotFound && (
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {t('home.search.notFound', { query: term })}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<PersonAddAlt1 />}
            onClick={handlers.openNewUser}
          >
            {t('home.search.newUser')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Domain />}
            onClick={handlers.openNewOrg}
          >
            {t('home.search.newOrg')}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default QuickCustomerSearch;
